import { MDSIconKind } from "@/mds";

export type BlockType =
  | "markdown"
  | "keyValue"
  | "taskList"
  | "table"
  | "noteList"
  | "noteShortcut"
  | "expandedNote"
  | "timeline"
  | "dateTextLink"
  | "collectionShortcut"
  | "cardGrid"
  | "singleImage"
  | "multiImage"
  | "resourceList"
  | "resourceShortcut"
  | "calendarEvents"
  | "yearCalendar"
  | "monthCalendar"
  | "weekCalendar"
  | "dayAgenda";

interface BlockTypeMeta {
  type: BlockType;
  label: string;
  description: string;
  iconKind: MDSIconKind;
  family: "text" | "notes" | "visual" | "resources" | "calendar";
}

export const BLOCK_TYPE_LIST: BlockTypeMeta[] = [
  {
    type: "markdown",
    label: "Text",
    description: "Free-form prose",
    iconKind: MDSIconKind.AlignLeft,
    family: "text",
  },
  {
    type: "keyValue",
    label: "Key facts",
    description: "Labelled fields",
    iconKind: MDSIconKind.Table,
    family: "text",
  },
  {
    type: "taskList",
    label: "Tasks",
    description: "Checkable to-dos",
    iconKind: MDSIconKind.Checkbox,
    family: "text",
  },
  {
    type: "table",
    label: "Table",
    description: "Rows and columns",
    iconKind: MDSIconKind.Table,
    family: "text",
  },
  {
    type: "noteList",
    label: "Note list",
    description: "Relevant notes",
    iconKind: MDSIconKind.List,
    family: "notes",
  },
  {
    type: "noteShortcut",
    label: "Note shortcut",
    description: "One compact note link",
    iconKind: MDSIconKind.Document,
    family: "notes",
  },
  {
    type: "expandedNote",
    label: "Expanded note",
    description: "One note with body",
    iconKind: MDSIconKind.FileAlt,
    family: "notes",
  },
  {
    type: "timeline",
    label: "Timeline",
    description: "Dated sequence",
    iconKind: MDSIconKind.Timeline,
    family: "text",
  },
  {
    type: "dateTextLink",
    label: "Date rows",
    description: "Date, text, and link",
    iconKind: MDSIconKind.Link,
    family: "text",
  },
  {
    type: "collectionShortcut",
    label: "Collection shortcut",
    description: "One collection link",
    iconKind: MDSIconKind.Collection,
    family: "notes",
  },
  {
    type: "cardGrid",
    label: "Card grid",
    description: "People or item cards",
    iconKind: MDSIconKind.Cells,
    family: "visual",
  },
  {
    type: "singleImage",
    label: "Image",
    description: "One image",
    iconKind: MDSIconKind.Image,
    family: "visual",
  },
  {
    type: "multiImage",
    label: "Image grid",
    description: "Multiple images",
    iconKind: MDSIconKind.GalleryThumbnails,
    family: "visual",
  },
  {
    type: "resourceList",
    label: "Resource list",
    description: "PDFs, links, notes",
    iconKind: MDSIconKind.Paperclip,
    family: "resources",
  },
  {
    type: "resourceShortcut",
    label: "Resource shortcut",
    description: "One PDF, link, or note",
    iconKind: MDSIconKind.Link,
    family: "resources",
  },
  {
    type: "calendarEvents",
    label: "Upcoming events",
    description: "Calendar event list",
    iconKind: MDSIconKind.CalendarEvent,
    family: "calendar",
  },
  {
    type: "yearCalendar",
    label: "Year calendar",
    description: "Year overview",
    iconKind: MDSIconKind.Calendar,
    family: "calendar",
  },
  {
    type: "monthCalendar",
    label: "Month calendar",
    description: "Month grid",
    iconKind: MDSIconKind.CalendarDay,
    family: "calendar",
  },
  {
    type: "weekCalendar",
    label: "Week calendar",
    description: "Week schedule",
    iconKind: MDSIconKind.CalendarCheck,
    family: "calendar",
  },
  {
    type: "dayAgenda",
    label: "Day agenda",
    description: "Single-day schedule",
    iconKind: MDSIconKind.CalendarDay,
    family: "calendar",
  },
];

export const iconForBlockType = (type: string | null | undefined): MDSIconKind => {
  return BLOCK_TYPE_LIST.find(b => b.type === type)?.iconKind ?? MDSIconKind.AlignLeft;
};
